/**
 * Parseur de fichier `.env`, pour l'import de variables d'environnement depuis
 * le poste de l'utilisateur.
 *
 * Le fichier ne quitte JAMAIS le navigateur : il est lu par la File API et
 * alimente le formulaire. L'API NubieCloud recoit ensuite les variables par le
 * chemin normal (`environment_variables` / `sensitive_variables`). Aucun
 * endpoint d'upload n'existe, et il ne faut pas en creer un : ce fichier
 * contient des secrets, le transporter deux fois double la surface.
 *
 * Rien n'est jete en silence : toute ligne ecartee ressort dans `skipped` avec
 * son numero et la raison, pour etre montree a l'utilisateur.
 */

/** Miroir de `K8S_CONFIG_KEY_REGEX` (app/schemas/deployment.py).
 *
 * Une cle de ConfigMap ou de Secret Kubernetes n'accepte que ces caracteres.
 * Cote API le rejet est un 422 ; le refuser ici evite un aller-retour et un
 * message d'erreur qui ne designe pas la ligne fautive. Toute evolution de la
 * regle cote back doit etre repercutee ici. */
export const K8S_CONFIG_KEY_REGEX = /^[-._a-zA-Z0-9]+$/;

export interface DotenvEntry {
  name: string;
  value: string;
}

export interface DotenvSkipped {
  /** Numero de ligne, 1-indexe : c'est ce que l'utilisateur voit dans son editeur. */
  line: number;
  raw: string;
  reason: 'sans_egal' | 'nom_invalide' | 'nom_vide';
}

export interface DotenvParseResult {
  entries: DotenvEntry[];
  skipped: DotenvSkipped[];
  /** Cles presentes plusieurs fois dans le fichier. La DERNIERE gagne, comme
   *  le ferait un shell qui source le fichier. */
  duplicates: string[];
}

/** Retire les guillemets englobants et interprete les echappements.
 *
 * Seuls les guillemets DOUBLES interpretent `\n` & co, comme dans un shell :
 * en simples, `\n` est deux caracteres et doit le rester. */
function unquote(raw: string): string {
  const v = raw.trim();
  if (v.length >= 2 && v[0] === '"' && v[v.length - 1] === '"') {
    return v
      .slice(1, -1)
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }
  if (v.length >= 2 && v[0] === "'" && v[v.length - 1] === "'") {
    return v.slice(1, -1);
  }
  // Valeur nue : un `#` precede d'un espace ouvre un commentaire de fin de
  // ligne. Sans l'espace (`pass#1`), c'est un caractere de la valeur -- un mot
  // de passe en contient souvent.
  const sansCommentaire = v.replace(/\s+#.*$/, '');
  return sansCommentaire.trim();
}

export function parseDotenv(contenu: string): DotenvParseResult {
  const entries: DotenvEntry[] = [];
  const skipped: DotenvSkipped[] = [];
  const vues = new Map<string, number>();
  const duplicates: string[] = [];

  // BOM (les editeurs Windows en posent un) + CRLF.
  const lignes = contenu.replace(/^﻿/, '').split(/\r?\n/);

  lignes.forEach((brute, i) => {
    const ligne = brute.trim();
    if (!ligne || ligne.startsWith('#')) return;

    const sansExport = ligne.replace(/^export\s+/, '');
    const egal = sansExport.indexOf('=');
    if (egal === -1) {
      skipped.push({ line: i + 1, raw: brute, reason: 'sans_egal' });
      return;
    }

    const nom = sansExport.slice(0, egal).trim();
    if (!nom) {
      skipped.push({ line: i + 1, raw: brute, reason: 'nom_vide' });
      return;
    }
    if (!K8S_CONFIG_KEY_REGEX.test(nom)) {
      skipped.push({ line: i + 1, raw: brute, reason: 'nom_invalide' });
      return;
    }

    const valeur = unquote(sansExport.slice(egal + 1));

    const dejaVue = vues.get(nom);
    if (dejaVue !== undefined) {
      if (!duplicates.includes(nom)) duplicates.push(nom);
      entries[dejaVue] = { name: nom, value: valeur };
      return;
    }
    vues.set(nom, entries.length);
    entries.push({ name: nom, value: valeur });
  });

  return { entries, skipped, duplicates };
}

/** Miroir du `encrypted_regex` de `app/sops/.sops.yaml`.
 *
 * C'est ce motif qui decide, cote git, quels champs SOPS chiffre. Il sert ici
 * a AVERTIR : une variable dont le nom y correspond, importee cote
 * configuration, partirait dans un ConfigMap -- lisible par quiconque a `get
 * configmap` sur le namespace. Et l'inverse est vrai : un secret dont le nom
 * n'y correspond PAS part en clair dans git, sans erreur.
 *
 * Avertissement seulement, jamais un blocage : c'est une heuristique sur un
 * nom, et l'utilisateur sait ce que contient sa variable. */
const MOTIF_SECRET = /^(authKey|password|secrets|adminPasswd|erlangCookie|api[Kk]ey|headscaleApiKey|smtp_auth_password|DATABASE_URL|.*[Pp]assword|.*[Tt]oken|.*_TOKEN|.*_KEY|.*_PASSWORD|.*_SECRET|.*[Ss]ecretAccessKey|.*[Aa]ccessKeyId|encryptionKey|secretKeyBase|htpasswd|webuiSecretKey|flaskSecretKey)$/;

export function ressembleAUnSecret(nom: string): boolean {
  return MOTIF_SECRET.test(nom);
}

export interface FusionResultat<T extends DotenvEntry> {
  variables: T[];
  ajoutees: number;
  remplacees: number;
}

/**
 * Fusionne les variables importees dans la liste affichee a l'ecran.
 *
 * Une cle deja presente est REMPLACEE : l'utilisateur qui importe un `.env`
 * veut que ce fichier fasse foi. Les lignes vides laissees par le bouton
 * « Ajouter » sont retirees, sinon l'import en laisse une au milieu.
 *
 * L'utilisateur supprime ensuite ce qu'il ne veut pas : c'est le flux demande,
 * et c'est pour ca que rien n'est filtre ici.
 */
export function fusionnerVariables<T extends DotenvEntry>(
  existantes: T[],
  importees: DotenvEntry[],
): FusionResultat<T> {
  const resultat = existantes.filter((v) => v.name !== '' || v.value !== '');
  let ajoutees = 0;
  let remplacees = 0;

  importees.forEach((imp) => {
    const i = resultat.findIndex((v) => v.name === imp.name);
    if (i === -1) {
      resultat.push({ ...imp } as T);
      ajoutees += 1;
    } else {
      resultat[i] = { ...resultat[i], value: imp.value };
      remplacees += 1;
    }
  });

  return { variables: resultat, ajoutees, remplacees };
}
